import { isValid, parse } from 'date-fns'

function extractFromText(
  text: string,
): { startDate: string; endDate: string } | null {
  // Pattern 1: Handle "from DD/MM/YYYY to DD/MM/YYYY" format (common in filters)
  const fromToRegex =
    /from\s+(\d{1,4}([/.-])\d{1,2}\2\d{2,4})\s+to\s+(\d{1,4}([/.-])\d{1,2}\4\d{2,4})/i
  const fromToMatch = text.match(fromToRegex)
  if (fromToMatch) {
    return { startDate: fromToMatch[1], endDate: fromToMatch[3] }
  }

  // Pattern 2: Handle "DD MMM YYYY to DD MMM YYYY" format (e.g., "02 Oct 2020 to 02 Oct 2025")
  const monthNameRegex =
    /\((\d{1,2}\s+\w{3}\s+\d{4})\s+to\s+(\d{1,2}\s+\w{3}\s+\d{4})\)/i
  const monthNameMatch = text.match(monthNameRegex)
  if (monthNameMatch) {
    return { startDate: monthNameMatch[1], endDate: monthNameMatch[2] }
  }

  // Pattern 3: Handle direct date ranges with '-' or ':' separators
  const dateRangeRegex =
    /(\d{1,4}([/.-])\d{1,2}\2\d{2,4})\s*[-:]\s*(\d{1,4}([/.-])\d{1,2}\4\d{2,4})/
  const rangeMatch = text.match(dateRangeRegex)
  if (rangeMatch) {
    return { startDate: rangeMatch[1], endDate: rangeMatch[3] }
  }

  // Pattern 4: Handle year-month range like "from 2022-10 to 2025-10"
  const yearMonthRangeRegex = /from\s+(\d{4}-\d{1,2})\s+to\s+(\d{4}-\d{1,2})/i
  const yearMonthMatch = text.match(yearMonthRangeRegex)
  if (yearMonthMatch) {
    const startYear = parseInt(yearMonthMatch[1].split('-')[0])
    const startMonth = parseInt(yearMonthMatch[1].split('-')[1])
    const endYear = parseInt(yearMonthMatch[2].split('-')[0])
    const endMonth = parseInt(yearMonthMatch[2].split('-')[1])

    const today = new Date()
    const currentYear = today.getFullYear()
    const currentMonth = today.getMonth() + 1
    const currentDay = today.getDate()

    const startDate = `${startYear}-${startMonth.toString().padStart(2, '0')}-01`

    let endDate: string
    if (endYear === currentYear && endMonth === currentMonth) {
      endDate = `${endYear}-${endMonth.toString().padStart(2, '0')}-${currentDay.toString().padStart(2, '0')}`
    } else {
      const lastDayOfMonth = new Date(endYear, endMonth, 0).getDate()
      endDate = `${endYear}-${endMonth.toString().padStart(2, '0')}-${lastDayOfMonth.toString().padStart(2, '0')}`
    }

    return { startDate, endDate }
  }

  // Pattern 5: Handle year-only range like "from 2024 to 2024" or "from 2021 to 2024"
  const yearOnlyRangeRegex = /from\s+(\d{4})\s+to\s+(\d{4})/i
  const yearOnlyMatch = text.match(yearOnlyRangeRegex)
  if (yearOnlyMatch) {
    const startYear = parseInt(yearOnlyMatch[1])
    const endYear = parseInt(yearOnlyMatch[2])
    const today = new Date()
    const currentYear = today.getFullYear()

    const startDate = `${startYear}-01-01`

    // If end year is current year, use today's date
    if (endYear === currentYear) {
      const currentMonth = (today.getMonth() + 1).toString().padStart(2, '0')
      const currentDay = today.getDate().toString().padStart(2, '0')
      return {
        startDate,
        endDate: `${currentYear}-${currentMonth}-${currentDay}`,
      }
    }

    // Otherwise use last day of end year
    return { startDate, endDate: `${endYear}-12-31` }
  }

  // Pattern 6: Handle "Year: from xxxx" (only start date)
  const yearFromRegex = /Year:\s*from\s+(\d{4})/i
  const yearFromMatch = text.match(yearFromRegex)
  if (yearFromMatch) {
    const year = parseInt(yearFromMatch[1])
    const today = new Date()
    const currentYear = today.getFullYear()
    const currentMonth = (today.getMonth() + 1).toString().padStart(2, '0')
    const currentDay = today.getDate().toString().padStart(2, '0')
    return {
      startDate: `${year}-01-01`,
      endDate: `${currentYear}-${currentMonth}-${currentDay}`,
    }
  }

  // Pattern 7: Handle "Year: to xxxx" (only end date)
  const yearToRegex = /Year:\s*to\s+(\d{4})/i
  const yearToMatch = text.match(yearToRegex)
  if (yearToMatch) {
    const year = parseInt(yearToMatch[1])
    const today = new Date()
    const currentYear = today.getFullYear()

    // If end year is current year, use today's date
    if (year === currentYear) {
      const currentMonth = (today.getMonth() + 1).toString().padStart(2, '0')
      const currentDay = today.getDate().toString().padStart(2, '0')
      return {
        startDate: '1900-01-01',
        endDate: `${currentYear}-${currentMonth}-${currentDay}`,
      }
    }

    // Otherwise use last day of end year
    return { startDate: '1900-01-01', endDate: `${year}-12-31` }
  }

  // Pattern 8: Handle single year like "Year: 2009" or "since 2025"
  const singleYearRegex = /(?:Year:\s*|since\s+)(\d{4})/i
  const singleYearMatch = text.match(singleYearRegex)
  if (singleYearMatch) {
    const year = parseInt(singleYearMatch[1])
    const today = new Date()
    const currentYear = today.getFullYear()

    // If it's "since" or the year is current/future, extend to today
    if (text.toLowerCase().includes('since') || year >= currentYear) {
      const currentMonth = (today.getMonth() + 1).toString().padStart(2, '0')
      const currentDay = today.getDate().toString().padStart(2, '0')
      return {
        startDate: `${year}-01-01`,
        endDate: `${currentYear}-${currentMonth}-${currentDay}`,
      }
    }

    // For past years, use full year range
    return { startDate: `${year}-01-01`, endDate: `${year}-12-31` }
  }

  // Pattern 9: Handle PubMed's date range format like "2020/10/02:2025/10/02[pdat]"
  const pubmedRangeRegex = /(\d{4}\/\d{1,2}\/\d{1,2}):(\d{4}\/\d{1,2}\/\d{1,2})/
  const pubmedMatch = text.match(pubmedRangeRegex)
  if (pubmedMatch) {
    return { startDate: pubmedMatch[1], endDate: pubmedMatch[2] }
  }

  return null
}
export function extractSearchDates(
  query: string | null,
  filters: string | null,
): { startDate: string; endDate: string } | null {
  if (query) {
    const result = extractFromText(query)
    if (result) return result
  }

  if (filters) {
    const result = extractFromText(filters)
    if (result) return result
  }

  return null
}

export function parseDate(dateStr: string): Date | null {
  const formats = [
    'dd MMM yyyy',
    'dd MMM yy',
    'd MMM yyyy',
    'd MMM yy',
    'MMM dd yyyy',
    'MMM dd yy',
    'MMM d yyyy',
    'MMM d yy',
    'dd MMMM yyyy',
    'dd MMMM yy',
    'd MMMM yyyy',
    'd MMMM yy',
    'MMMM dd yyyy',
    'MMMM dd yy',
    'MMMM d yyyy',
    'MMMM d yy',
    'yyyy-MM-dd',
    'yyyy-M-d',
    'yyyy-MM-d',
    'yyyy-M-dd',
    'yyyy/MM/dd',
    'yyyy.MM.dd',
    'yyyy/M/d',
    'yyyy-M-d',
    'yyyy.M.d',
    'dd/MM/yyyy',
    'dd-MM-yyyy',
    'dd.MM.yyyy',
    'MM/dd/yyyy',
    'MM-dd-yyyy',
    'MM.dd.yyyy',
    'dd/M/yyyy',
    'dd-M-yyyy',
    'dd.M.yyyy',
    'M/dd/yyyy',
    'M-dd-yyyy',
    'M.dd.yyyy',
  ]

  for (const format of formats) {
    const parsedDate = parse(dateStr, format, new Date())
    if (isValid(parsedDate)) {
      return parsedDate
    }
  }
  return null
}

export function normalizeDate(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}
